import React, { useState } from 'react';
import { NCZIElectronicPrescriptionItem } from '../../../Interfaces/ncziElectronicPrescriptionItem';
import NZCIListItem from '../../Base/NZCIListItem';
import FormattedDate, { FormattedDateTime } from '../../../../../Components/FormattedDate';
import useTranslation from '../../../../../Hooks/useTranslation';
import ElectronicPrescriptionSignatureStatus from './ElectronicPrescriptionSignatureStatus';
import Barcode from 'react-barcode';

//@ts-ignore
import styles from './ElectronicPrescriptionDetail.module.scss';
import RemoveButton from '../../../../../Components/Button/RemoveButton';
import { CancelExaminationResult } from '../../../Commands/CancelExaminationResult';
import moment from 'moment-timezone';
import { StornoPatientElectronicPrescription } from '../../../Commands/StornoPatientElectronicPrescription';
import useNCZICommandProcess from '../../../Hooks/useNCZICommandProcess';
import NCZICommandResult from '../../NCZICommandResult/NCZICommandResult';
import { useNCZIContext } from '../../../Services/NCZIContext';
import ElectronicPrescriptionDailyDosageFixed from './ElectronicPrescriptionDailyDosageFixed';
import ElectronicPrescriptionDailyDosageHourly from './ElectronicPrescriptionDailyDosageHourly';
import { InvalidatePatientElectronicPrescription } from '../../../Commands/InvalidatePatientElectronicPrescription';

interface Props {
  item: NCZIElectronicPrescriptionItem;
  onClose: (needReload: boolean) => void;
}

const ElectronicPrescriptionDetail: React.FC<Props> = (props) => {
  const {item, onClose} = props;
  const {t} = useTranslation();
  const {patientInsurance} = useNCZIContext();

  const { processingCommandId, processCommand, isProcessing } =
    useNCZICommandProcess({
      onFinish: () => onClose?.(true),
    });

  const handleStornoPrescription = async () => {
    const stornoCommand = StornoPatientElectronicPrescription({
      birthNumber: item.RodneCisloVykazane.toString(),
      prescriptionCode: item.CiarovyKod,
      insurance: patientInsurance!
    });

    await processCommand(stornoCommand);
  };

  const handleInvalidatePrescription = async () => {
    const stornoCommand = InvalidatePatientElectronicPrescription({
      birthNumber: item.RodneCisloVykazane.toString(),
      prescriptionCode: item.CiarovyKod,
      insurance: patientInsurance!
    });

    await processCommand(stornoCommand);
  };

  if (isProcessing && processingCommandId) {
    return <NCZICommandResult commandId={processingCommandId} />;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.barcode}>
        <Barcode value={item.CiarovyKod} format={"CODE128"} />
      </div>
      <br />
      <NZCIListItem title='nczi.createdAt' value={<FormattedDate date={item.Datum2Vykazany} />} />
      <NZCIListItem title='nczi.electronicPrescription.detail.datetimeEvidence' value={item.DatumCasEvidencie ? <FormattedDateTime datetime={item.DatumCasEvidencie} /> : '-'} />
      <NZCIListItem title='nczi.electronicPrescription.detail.lastChane' value={item.DatumPoslednejModifikacie ? <FormattedDateTime datetime={item.DatumPoslednejModifikacie} /> : '-'} />
      <br />
      <NZCIListItem title='nczi.electronicPrescription.detail.birthNumber' value={item.RodneCisloVykazane} />
      <NZCIListItem title='nczi.electronicPrescription.detail.validTo' value={<FormattedDate date={item.PlatnostReceptu} />} />
      {item.PeriodaOpakovanehoReceptu && <>
        <br />
        <NZCIListItem title='nczi.electronicPrescription.detail.useRepeat' value={""} />
        <NZCIListItem title='nczi.electronicPrescription.detail.repeat.period' value={item.PeriodaOpakovanehoReceptu} />
        <NZCIListItem title='nczi.electronicPrescription.detail.repeat.count' value={item.PocetOpakovaniOpakovanehoReceptu} />
      </>}
      <br />
      <NZCIListItem title='nczi.electronicPrescription.detail.diagnosisCode' value={item.KodDiagnozaVykazana + (item.DiagnozaVykazana ? ' - ' + item.DiagnozaVykazana.displayName : '')} />
      <br />
      {item.UdajeProdukt.ProduktPZSVykaz && <NZCIListItem title='nczi.electronicPrescription.detail.productCode' value={<>
        {item.UdajeProdukt.ProduktPZSVykaz.code}: <strong>{item.UdajeProdukt.ProduktPZSVykaz.name}</strong> {item.UdajeProdukt.ProduktPZSVykaz.additionalName && <>({item.UdajeProdukt.ProduktPZSVykaz.additionalName})</>}
      </>} />}
      {!item.UdajeProdukt.ProduktPZSVykaz && <NZCIListItem title='nczi.electronicPrescription.detail.productCode' value={item.UdajeProdukt.KodProduktPZSVykaz} />}
      <NZCIListItem title='nczi.electronicPrescription.detail.quantity' value={item.UdajeProdukt.MnozstvoVykazane} />
      {item.UdajeProdukt.MernaJednotka && <NZCIListItem title='nczi.electronicPrescription.detail.medicalDeviceUnit' value={item.UdajeProdukt.MernaJednotka} />}
      {item.OpisPomockyNaMieru && <NZCIListItem title='nczi.electronicPrescription.detail.medicalDeviceDescription' value={item.OpisPomockyNaMieru} />}
      {item.Zdovodnenie && <NZCIListItem title='nczi.electronicPrescription.detail.medicalDeviceJustification' value={item.Zdovodnenie} />}
      {item.AplikacnaZonaParovehoOrganu && <NZCIListItem title='nczi.electronicPrescription.detail.applicationZone.title' value={item.AplikacnaZonaParovehoOrganu === 'L' ? t('nczi.electronicPrescription.detail.applicationZone.left') : t('nczi.electronicPrescription.detail.applicationZone.right')} />}
      <br />
      {typeof item.UdajeIPL !== 'undefined' && <>
        <NZCIListItem title='nczi.electronicPrescription.detail.ipl.composition' value={item.UdajeIPL.ZlozenieIPL} />
        <NZCIListItem title='nczi.electronicPrescription.detail.ipl.prepareInstruction' value={item.UdajeIPL.PokynyPriVyrobeIPL} />
        <NZCIListItem title='nczi.electronicPrescription.detail.ipl.useInstruction' value={item.UdajeIPL.PokynKUzivaniuIPL} />
        <br />
      </>}
      <NZCIListItem title='nczi.electronicPrescription.detail.dosage.title' value={""} />
      <NZCIListItem title='nczi.electronicPrescription.detail.dosage.type' value={item.Davkovanie.TypDavkovania.displayName} />
      {item.Davkovanie.DenneDavkovanie1 && <NZCIListItem title='nczi.electronicPrescription.detail.dosage.dailyType' value={<ElectronicPrescriptionDailyDosageFixed value={item.Davkovanie.DenneDavkovanie1.DenneDavkovanie1} />} />}
      {item.Davkovanie.DenneDavkovanie2 && <NZCIListItem title='nczi.electronicPrescription.detail.dosage.dailyType' value={<ElectronicPrescriptionDailyDosageHourly value={item.Davkovanie.DenneDavkovanie2} />} />}
      {item.Davkovanie.JednotkaDavkovania && <NZCIListItem title='nczi.electronicPrescription.detail.dosage.unit' value={item.Davkovanie.JednotkaDavkovania.displayName} />}
      {item.Davkovanie.Periodicita && <NZCIListItem title='nczi.electronicPrescription.detail.dosage.periodicity' value={item.Davkovanie.Periodicita.displayName} />}
      {item.Davkovanie.SposobPodania && <NZCIListItem title='nczi.electronicPrescription.detail.dosage.methodOfAdministration' value={item.Davkovanie.SposobPodania.displayName} />}
      <NZCIListItem title='nczi.electronicPrescription.detail.dosage.note' value={item.Davkovanie.Poznamka || '-'} />
      <NZCIListItem title='nczi.electronicPrescription.detail.dosage.transcription' value={item.Davkovanie.PrepisDavkovania} />
      <br />
      <NZCIListItem title='nczi.electronicPrescription.detail.nonPayer' value={item.Neplatic ? t('base.yes') : t('base.no')} />
      <NZCIListItem title='nczi.electronicPrescription.detail.permanentlyAffected' value={item.TrvalePostihnutie ? t('base.yes') : t('base.no')} />
      <NZCIListItem title='nczi.electronicPrescription.detail.newbornToParentBN' value={item.NovorodenecNaRCRodica ? t('base.yes') : t('base.no')} />
      <NZCIListItem title='nczi.electronicPrescription.detail.productOnException' value={item.ProduktNaVynimku ? t('base.yes') : t('base.no')} />
      <NZCIListItem title='nczi.electronicPrescription.detail.urgentEmergency' value={item.NeodkladnaZS ? t('base.yes') : t('base.no')} />
      <NZCIListItem title='nczi.electronicPrescription.detail.prohibitionOfReplacement' value={item.ZakazVydaniaNahrady ? t('base.yes') : t('base.no')} />
      {item.DovodUhradyPacienta && <NZCIListItem title='nczi.electronicPrescription.detail.patientPayment' value={item.DovodUhradyPacienta === 'JASAM' ? t('base.yes') : t('base.no')} />}
      {/*<NZCIListItem title='nczi.electronicPrescription.detail.atc' value={} />*/}
      {/*<NZCIListItem title='nczi.electronicPrescription.detail.atcAddon' value={} />*/}
      {item.OdporucajuciLekar?.KodOdporucajuciLekar && <>
        <br />
        <NZCIListItem title='nczi.electronicPrescription.detail.useReferrer' value={""} />
        <NZCIListItem title='nczi.electronicPrescription.detail.referrer.code' value={item.OdporucajuciLekar.KodOdporucajuciLekar} />
        <NZCIListItem title='nczi.electronicPrescription.detail.referrer.codeNZ' value={item.OdporucajuciLekar.KodOdporucajucaNZ} />
        <NZCIListItem title='nczi.electronicPrescription.detail.referrer.date' value={<FormattedDate date={item.OdporucajuciLekar.DatumOdporucania} />} />
      </>}
      <br />
      <NZCIListItem title='nczi.electronicPrescription.detail.companyIdentificationNumber' value={item.PredpisujuciLekar.ICOHlavnaZlozka} />
      <NZCIListItem title='nczi.electronicPrescription.detail.doctorCode' value={item.PredpisujuciLekar.KodPredpisujuciLekar} />
      <NZCIListItem title='nczi.electronicPrescription.detail.status' value={<ElectronicPrescriptionSignatureStatus status={item.SignatureValidationStatus} />} />

      <br />
      {item.PeriodaOpakovanehoReceptu && <RemoveButton
        title={'nczi.action.invalidatePrescription'}
        onClick={() => handleInvalidatePrescription()}
        showConfirmation={false}
      />}
      {!item.PeriodaOpakovanehoReceptu && <RemoveButton
        title={'nczi.action.cancelPrescription'}
        onClick={() => handleStornoPrescription()}
        showConfirmation={false}
      />}

    </div>
  );
};

export default ElectronicPrescriptionDetail;
